import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import A from 'wanderu-component-react-router-link';
import Tags from 'wanderu-component-tags';
import {
    Button,
    List,
    ListItem,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';
import { buildSearchUrl } from 'wanderu-utils';

import notFoundChiku from 'wanderu-assets/images/not-found-chiku.svg';

import {
    ALTERNATIVE_SEARCH_DAYS,
    DEPART_DIRECTION,
} from '../../../constants';
import Events from '../events/SearchListEvents';

import styles from './NoResults.scss';

function _buildSearchUrl(searchParams, searchDate) {
    const {
        direction,
        urlFilterParams,
        originPlaceId,
        destinationPlaceId,
        originText,
        destinationText,
        departTripId,
    } = searchParams;

    const opts = {
        direction,
        departDate: direction === DEPART_DIRECTION
            ? searchDate
            : searchParams.departDate,
        returnDate: direction === DEPART_DIRECTION
            ? searchParams.returnDate
            : searchDate,
        originText,
        destinationText,
        originPlaceId,
        destinationPlaceId,
        departTripId,
    };

    return buildSearchUrl(opts, urlFilterParams);
}

function createTagObjects(props) {
    const {
        t,
        isDirectOnlyFiltered,
        isAllowNearbyFiltered,
        isFeaturesFiltered,
        isTravelModesFiltered,
        isDepartureTimingFiltered,
        isArrivalTimingFiltered,
        isArrivalStationsFiltered,
        isDepartureStationsFiltered,
        resetArrivalStations,
        resetDepartureStations,
        resetDepartureTiming,
        resetArrivalTiming,
        resetTravelModes,
        resetFeatures,
        resetDirectOnly,
        resetAllowNearby,
    } = props;

    return [
        isDepartureTimingFiltered && {
            text: t('departure-time'),
            onClick: resetDepartureTiming,
        },
        isArrivalTimingFiltered && {
            text: t('arrival-time'),
            onClick: resetArrivalTiming,
        },
        isTravelModesFiltered && {
            text: t('travel-mode'),
            onClick: resetTravelModes,
        },
        isArrivalStationsFiltered && {
            text: t('locations-filter-header'),
            onClick: resetArrivalStations,
        },
        isDepartureStationsFiltered && {
            text: t('locations-filter-header'),
            onClick: resetDepartureStations,
        },
        isFeaturesFiltered && {
            text: t('amenities'),
            onClick: resetFeatures,
        },
        isDirectOnlyFiltered && {
            text: t('direct-only'),
            onClick: resetDirectOnly,
        },
        isAllowNearbyFiltered && {
            text: t('nearby-locations'),
            onClick: resetAllowNearby,
        },
    ].filter((tag) => !!tag);
}

function DateField(props) {
    const {
        locale,
        date,
        price,
        searchParams,
        i18nMoment,
        i18nMoney,
    } = props;

    const dateString = i18nMoment(date);
    const searchDate = i18nMoment(date).format('YYYY-MM-DD');
    const monthAndDate = dateString.shortDayAndWeekday(locale);
    const priceString = i18nMoney(price).format({
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
    });

    return (
        <A
            className={styles.dateButton}
            href={_buildSearchUrl(searchParams, searchDate)}
            underline="none"
        >
            <span className={styles.label}>{monthAndDate}</span>
            <span className={styles.monthAndDateLabel}>{priceString}</span>
        </A>
    );
}

DateField.propTypes = {
    date: PropTypes.string.isRequired,
    i18nMoment: PropTypes.func.isRequired,
    i18nMoney: PropTypes.func.isRequired,
    locale: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    searchParams: PropTypes.object.isRequired,
};

const LocalizedDateField = localize(DateField);

function AlternativeSearchDays(props) {
    const {
        locale,
        alternativeSearchDays,
        searchParams,
    } = props;

    // put snowplow event here
    const onClick = (date) => {
        Events.dateSuggestionSelected(
            {
                departDate: searchParams.departDate,
                destinationPlaceId: searchParams.destinationPlaceId,
                destinationText: searchParams.destinationText,
                originPlaceId: searchParams.originPlaceId,
                originText: searchParams.originText,
                suggestionDate: date,
            }
        );
    };

    return (
        <List
            className={styles.altSearchDaysContainer}
            component="ol"
        >
            {Object.entries(alternativeSearchDays)
                .map(([date, price], index) => {
                    if (index > ALTERNATIVE_SEARCH_DAYS) {
                        return null;
                    }

                    return (
                        <ListItem
                            className={styles.altSearchDay}
                            key={date}
                            onClick={() => onClick(date)}
                        >
                            <LocalizedDateField
                                date={date}
                                locale={locale}
                                price={price}
                                searchParams={searchParams}
                            />
                        </ListItem>
                    );
                })}
        </List>
    );
}

AlternativeSearchDays.propTypes = {
    alternativeSearchDays: PropTypes.object,
    locale: PropTypes.string,
    searchParams: PropTypes.object,
};

function NoResults(props) {
    const {
        t,
        locale,
        searchParams,
        className,
        showReset,
        alternativeSearchDays,
        inlineSearchAds,
        onResetAllFilters,
        isAllowNearbyFiltered,
        isArrivalTimingFiltered,
        isDepartureTimingFiltered,
        isDirectOnlyFiltered,
        isFeaturesFiltered,
        isTravelModesFiltered,
        resetAllowNearby,
        resetArrivalTiming,
        resetDepartureTiming,
        resetDirectOnly,
        resetFeatures,
        resetTravelModes,
    } = props;

    const tagObjects = createTagObjects(props);
    const showAltSearchDays = !!Object.keys(alternativeSearchDays).length;
    const chikuMessageBottom = showAltSearchDays
        ? t('no-search-results-subtext-other-dates')
        : t('no-search-results-subtext-new');

    return (
        <div className={classNames(styles.chikuContainer, className)}>
            <img
                alt={t('sad-chiku')}
                className={styles.sadChiku}
                role="presentation"
                src={notFoundChiku}
            />
            <div className={styles.chikuMessageTop}>
                {showReset
                    ? t('no-filtered-results')
                    : t('no-search-results')}
            </div>
            <div className={styles.chikuMessageBottom}>
                {showReset
                    ? t('no-filtered-results-subtext-new')
                    : chikuMessageBottom}
            </div>
            {showReset && (
                <>
                    <div className={styles.tagContainer}>
                        <Tags
                            className={styles.tags}
                            tags={tagObjects}
                        />
                    </div>
                    <Button
                        className={styles.resetButton}
                        onClick={onResetAllFilters}
                        unstyled
                    >
                        {t('reset-all')}
                    </Button>
                </>
            )}
            {showAltSearchDays && (
                <AlternativeSearchDays
                    alternativeSearchDays={alternativeSearchDays}
                    locale={locale}
                    searchParams={searchParams}
                />
            )}
            {inlineSearchAds.length > 0 && (
                <div>
                    {inlineSearchAds.map(({ ad }) => ad)}
                </div>
            )}
        </div>
    );
}

NoResults.propTypes = {
    alternativeSearchDays: PropTypes.object,
    className: PropTypes.string,
    inlineSearchAds: PropTypes.arrayOf(PropTypes.object),
    isAllowNearbyFiltered: PropTypes.bool,
    isArrivalTimingFiltered: PropTypes.bool,
    isDepartureTimingFiltered: PropTypes.bool,
    isDirectOnlyFiltered: PropTypes.bool,
    isFeaturesFiltered: PropTypes.bool,
    isTravelModesFiltered: PropTypes.bool,
    locale: PropTypes.string,
    onResetAllFilters: PropTypes.func,
    resetAllowNearby: PropTypes.func,
    resetArrivalTiming: PropTypes.func,
    resetDepartureTiming: PropTypes.func,
    resetDirectOnly: PropTypes.func,
    resetFeatures: PropTypes.func,
    resetTravelModes: PropTypes.func,
    searchParams: PropTypes.object,
    showReset: PropTypes.bool,
    t: PropTypes.func.isRequired,
};

NoResults.defaultProps = {
    alternativeSearchDays: {},
    className: '',
    inlineSearchAds: [],
    isAllowNearbyFiltered: false,
    isArrivalTimingFiltered: false,
    isDepartureTimingFiltered: false,
    isDirectOnlyFiltered: false,
    isFeaturesFiltered: false,
    isTravelModesFiltered: false,
    onResetAllFilters() {},
    resetAllowNearby() {},
    resetArrivalTiming() {},
    resetDepartureTiming() {},
    resetDirectOnly() {},
    resetFeatures() {},
    resetTravelModes() {},
    searchParams: {},
    showReset: false,
};

export { NoResults };
export default localize(NoResults, 'baseApp');
